/**
 * OrgContext — the active organization for the signed-in user.
 *
 * Every API call that takes an org_id reads it from this context. The first
 * page load resolves the user's memberships via `client.me.get()` and
 * augments each entry with the org's display name (fetched in parallel
 * because the `/v1/me` summary only carries org_id + role).
 *
 * The active org id is persisted under `wk_active_org` in localStorage so a
 * reload preserves the user's choice. If the persisted value points at an
 * org the user is no longer a member of, we silently fall back to the first
 * membership — never crash.
 *
 * When the user has zero memberships, `activeOrg` is `null` and
 * `needsOrg` is `true`. Routes that require an org must gate on this and
 * show the create-org flow before issuing org-scoped requests.
 */

import type { MemberRole, OrgRecord } from "@wardkey/sdk";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useToast } from "../toast";
import { useApiClient } from "./client";

export const ACTIVE_ORG_STORAGE_KEY = "wk_active_org";

export type Membership = {
  org_id: string;
  role: MemberRole;
  /** Display name from `OrgRecord.name`. Falls back to org_id while loading. */
  name: string;
};

export type OrgContextValue = {
  /** The currently selected org, or null when the user has zero memberships. */
  activeOrg: Membership | null;
  /** Switch the active org. No-op if the id is not in `memberships`. */
  setActiveOrg: (orgId: string) => void;
  memberships: ReadonlyArray<Membership>;
  /** True while the initial me + org-record fan-out is in flight. */
  isLoading: boolean;
  /** True when the user has no memberships and must be funnelled through create-org. */
  needsOrg: boolean;
  /** Re-fetch memberships — used after a successful org create. */
  refresh: () => void;
};

const OrgContext = createContext<OrgContextValue | null>(null);

type Props = {
  children: ReactNode;
  /**
   * Override the storage backend. Defaults to `window.localStorage`. Tests
   * inject an in-memory shim; SSR/private-mode could fall back to a no-op.
   */
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
};

function safeStorage(): Pick<Storage, "getItem" | "setItem" | "removeItem"> {
  try {
    if (typeof window !== "undefined" && window.localStorage) return window.localStorage;
  } catch {
    // Browsers throw on `window.localStorage` access in some privacy modes.
  }
  return {
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {},
  };
}

export function OrgProvider({ children, storage }: Props) {
  const client = useApiClient();
  const { pushToast } = useToast();
  const store = useMemo(() => storage ?? safeStorage(), [storage]);

  const [memberships, setMemberships] = useState<ReadonlyArray<Membership>>([]);
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // Stash pushToast in a ref so the load effect doesn't re-run on every render
  // when the toast provider mints a new function identity.
  const pushToastRef = useRef(pushToast);
  pushToastRef.current = pushToast;

  // biome-ignore lint/correctness/useExhaustiveDependencies: nonce is a refresh ticker — incrementing it is how callers re-run this effect
  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const me = await client.me.get();
        if (cancelled) return;

        // Fan-out org-record fetches in parallel. If any one fails the
        // dropdown still works using the org_id as the visible label.
        const records = await Promise.allSettled(
          me.org_memberships.map((m) => client.orgs.get(m.org_id)),
        );
        if (cancelled) return;

        const list: Membership[] = me.org_memberships.map((m, i) => {
          const r = records[i];
          const name = r && r.status === "fulfilled" ? (r.value as OrgRecord).name : m.org_id;
          return { org_id: m.org_id, role: m.role, name };
        });

        setMemberships(list);

        // Resolve the active org: persisted choice wins if still valid,
        // otherwise the first membership, otherwise null.
        const persisted = store.getItem(ACTIVE_ORG_STORAGE_KEY);
        const valid = persisted && list.some((m) => m.org_id === persisted) ? persisted : null;
        const next = valid ?? list[0]?.org_id ?? null;
        setActiveOrgIdState(next);
        if (next) store.setItem(ACTIVE_ORG_STORAGE_KEY, next);
        else store.removeItem(ACTIVE_ORG_STORAGE_KEY);

        setIsLoading(false);
      } catch (err) {
        if (cancelled || ctrl.signal.aborted) return;
        setIsLoading(false);
        const message = err instanceof Error ? err.message : "Failed to load organizations";
        pushToastRef.current({ kind: "error", title: message });
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [client, store, nonce]);

  const setActiveOrg = useCallback(
    (orgId: string) => {
      setMemberships((current) => {
        if (!current.some((m) => m.org_id === orgId)) return current;
        setActiveOrgIdState(orgId);
        store.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
        return current;
      });
    },
    [store],
  );

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const value = useMemo<OrgContextValue>(() => {
    const activeOrg = memberships.find((m) => m.org_id === activeOrgId) ?? null;
    return {
      activeOrg,
      setActiveOrg,
      memberships,
      isLoading,
      needsOrg: !isLoading && memberships.length === 0,
      refresh,
    };
  }, [memberships, activeOrgId, setActiveOrg, isLoading, refresh]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg must be called inside <OrgProvider />");
  return ctx;
}

/**
 * Convenience for routes that must have an active org. Throws if called
 * before the create-org gate has resolved — call sites that gate on
 * `needsOrg`/`isLoading` first will never hit this.
 */
export function useActiveOrg(): Membership {
  const { activeOrg } = useOrg();
  if (!activeOrg) {
    throw new Error("useActiveOrg called without an active org — gate on needsOrg first");
  }
  return activeOrg;
}
